// Form errors
export type FormErrors = Partial<Record<keyof FormValues, string>>;

// Maps to: "Do you work for a Solution or Marketplace Partner?"
export type PartnerType = 'No' | 'Solution Partner' | 'Marketplace Partner';

// Maps to: "How do you want to contribute as an Atlassian Champion?"
export type ChampionPreference =
  | 'community_forums_contributor' // Contributing to Atlassian Community Forums
  | 'community_event_organizer' // Organizing Atlassian Community Events
  | 'community_content_creator' // Creating original thought leadership or learning content
  | 'success_training_builder' // Creating or delivering Atlassian training for end users
  | 'success_cug_leader' // Running Atlassian-focused CUG / training events
  | 'success_coe_contributor' // Contributing in a Center of Excellence
  | null;

export type YesNo = 'yes' | 'no' | null;

export type YesNoUnsure = 'yes' | 'no' | 'unsure' | null;

// Maps to: "What's the focus of your Center of Excellence?"
export type CoeFocus = 'Project Management' | 'Agile Methodologies' | 'Other' | null;

export type TrainingSource =
  | 'atlassian_on_demand'
  | 'solution_partner'
  | 'in_house_training'
  | 'self_created'
  | 'other';

// How did you find out about the Atlassian Champions program?
export type HowHeard =
  | 'ea'
  | 'tam'
  | 'csm'
  | 'employee'
  | 'champion'
  | 'solution_partner'
  | 'community_website'
  | 'email_from_atlassian'
  | 'corporate_event'
  | 'social_media'
  | 'coworker'
  | 'other_user_group'
  | 'other'
  | null;

// -----------------------------
// Full form data structure
// -----------------------------

export type FormValues = {
  // 1. Personal info + contact (top of Salesforce form)
  firstName: string;
  lastName: string;
  email: string;
  communityProfileUrl: string;
  linkedinUrl: string;

  // 2. Address + birthdate
  street: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
  // Text input with placeholder "MM/DD/YYYY", same as Salesforce
  birthDate: string;

  // 3. Work & partner info (checkbox group — multiple selections allowed)
  partnerType: PartnerType[];

  // 4. Atlassian products used (checkbox group)
  products: string[]; // will hold IDs like 'jira', 'confluence', etc.
  // "Please specify" when "Other" is selected (Salesforce: otherAtlassianProducts)
  otherAtlassianProducts: string;

  // 5. Global motivation
  // Maps to: "Why would you like to become an Atlassian Champion?"
  whyBecomeChampion: string;

  // 6. Single top preference (one of the six tracks)
  championPreference: ChampionPreference;

  // --- Contributing to Atlassian Community Forums (conditional) ---
  // Maps to text area: "How do you see yourself contributing to the Community Forums?"
  howContributeOnline: string;

  // --- Organizing Atlassian Community Events (conditional) ---
  // "Have you previously attended an Atlassian Community Event?" (Yes/No)
  attendedAce: YesNo;
  // "Please tell us about your experience." (conditional: visible when attendedAce === 'yes')
  aceExperience: string;
  // "Can you commit to host at least one event every 90 days…?"
  fourEventsPerYear: 'yes' | 'no' | 'maybe_need_more_info' | null;
  // "What kind of activities and events are you hoping to organize?"
  activityAndEvent: string;

  // --- Creating original thought leadership or learning content (conditional) ---
  // "Why do you create Atlassian-focused content on social media?"
  whyToCreateContent: string;
  // "How often do you currently share your own content…?" dropdown
  contentSharingFrequency:
    | 'less_than_once_a_month'
    | 'once_a_month'
    | 'every_two_weeks'
    | 'weekly'
    | 'multiple_times_a_week'
    | '';
  // "Share three examples of original content…"
  bestContent1: string;
  bestContent2: string;
  bestContent3: string;
  // "Primary social media profile URL"
  primarySocialProfile: string;
  // "Other social media profiles"
  otherURL: string;
  // "Can you commit to sharing 2 pieces of content per month?" (Yes/No)
  commitSharingContent: YesNo;

  // --- Creating or delivering Atlassian training for end users (conditional) ---
  // "How often do you build training on Atlassian for end users?"
  trainingFrequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly' | '';
  // "What does your organization currently use for Atlassian training? Select all that apply."
  trainingSources: TrainingSource[];

  // --- Running Atlassian-focused CUG / training events (conditional) ---
  // "Do you currently have an active Atlassian focused Company User Group…?"
  activeCug: YesNoUnsure;
  // "How many Company User Group sessions or other training events do you run each year?"
  eventsPerYear: 'up_to_4' | '5_to_9' | '10_plus' | '';

  // --- Contributing in a Center of Excellence ---
  // "Do you currently discuss Atlassian in a Center of Excellence?"
  discussInCoE: YesNo;
  // "What's the focus of your Center of Excellence?"
  coeFocus: CoeFocus;
  // "Other" (conditional: visible when coeFocus === 'other')
  otherExcellence: string;

  // 7. How did you find out about the Atlassian Champions program?
  howHeard: HowHeard;
  howHeardOther: string;
  // "Please specify" (conditional: visible when howHeard === 'employee')
  howHeardEmployee: string;
  // "Referrer Name" (conditional: visible when howHeard === 'champion')
  referrerName: string;

  // 8. Agreements (end of form)
  agreePrivacy: boolean;
  agreeNda: boolean;
};

// -----------------------------
// Initial form values (blank form)
// -----------------------------

export const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  communityProfileUrl: '',
  linkedinUrl: '',

  street: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  country: '',
  birthDate: '',

  partnerType: [],

  products: [],
  otherAtlassianProducts: '',

  whyBecomeChampion: '',

  championPreference: null,

  howContributeOnline: '',

  attendedAce: null,
  aceExperience: '',
  fourEventsPerYear: null,
  activityAndEvent: '',

  whyToCreateContent: '',
  contentSharingFrequency: '',
  bestContent1: '',
  bestContent2: '',
  bestContent3: '',
  primarySocialProfile: '',
  otherURL: '',
  commitSharingContent: null,

  trainingFrequency: '',
  trainingSources: [],

  activeCug: null,
  eventsPerYear: '',

  discussInCoE: null,
  coeFocus: null,
  otherExcellence: '',

  howHeard: null,
  howHeardOther: '',
  howHeardEmployee: '',
  referrerName: '',

  agreePrivacy: false,
  agreeNda: false,
};
